import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/content';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <div className="max-w-lg mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-2xl text-accent-500"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-900 mb-3">分类未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您要查找的分类不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageSEO
        title={`${category.name} - 海角最新地址`}
        description={`海角最新地址${category.description}，包含${category.articleCount}篇相关文章与教程。海角最新地址入口、海角最新地址更新。`}
        keywords={`海角最新地址,${category.name},海角地址,海角访问入口`}
        canonicalUrl={`https://haijiao.readdy.ai/category/${slug}`}
      />

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: category.name },
        ]}
      />

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className={`${category.icon} text-xl text-accent-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h2 className="font-heading text-base text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.publishedAt}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-accent-500"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}